/**
 * Single entry point for every network call the storefront makes.
 *
 * Two things happen here that the plain `fetch("/api/...")` calls could not do
 * once the API moved to a separate origin:
 *
 * 1. `credentials: "include"` so the httpOnly `mova.session-token` cookie is
 *    sent with every request (CORS on the API already allows credentials).
 * 2. Relative `/api/...` URLs are rewritten to `VITE_API_BASE_URL`, so the
 *    component code stays byte-identical to the Next.js version.
 */
const RAW_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** Same-origin by default; stripped of a trailing slash. */
export const API_BASE_URL = RAW_BASE.replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** `/api/products` → `${API_BASE_URL}/api/products` */
export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${suffix}`;
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /** Skip the JSON parse and return the raw `Response` (used by the webhook test). */
  raw?: boolean;
};

export async function apiFetch<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, raw, headers, ...init } = options;

  const response = await fetch(apiUrl(path), {
    ...init,
    credentials: "include",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (raw) return response as unknown as T;

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, extractMessage(payload, response), payload);
  }

  return payload as T;
}

/**
 * `GET`, which is what almost every call in the app is. Returns `null` instead
 * of throwing when the request fails, matching the defensive `try/catch` that
 * the components already had around their `fetch` calls.
 */
export async function apiGet<T = unknown>(path: string): Promise<T | null> {
  try {
    return await apiFetch<T>(path, { method: "GET" });
  } catch {
    return null;
  }
}

export const apiPost = <T = unknown>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: "POST", body });

export const apiPatch = <T = unknown>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: "PATCH", body });

export const apiDelete = <T = unknown>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: "DELETE", body });

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractMessage(payload: unknown, response: Response): string {
  if (payload && typeof payload === "object") {
    const message = (payload as { message?: unknown }).message;
    if (typeof message === "string") return message;
    if (Array.isArray(message)) return message.join(", ");
  }
  if (typeof payload === "string" && payload.trim()) return payload;
  return response.statusText || "Request failed";
}

/**
 * Vite dev proxies `/api` to the NestJS server, so the app works same-origin
 * without any configuration. In production set `VITE_API_BASE_URL` to the API
 * origin (for example `https://api.example.com`).
 */
export function usingDevProxy(): boolean {
  return API_BASE_URL === "";
}